import React, { useEffect, useState } from 'react';
import { Modal, Button, Icon, Form, Checkbox, Message, Label } from 'semantic-ui-react';
import type { ClientResponse } from '../../types/dtos';
import { daysSinceLastCnpjSync, CNPJ_SYNC_STALE_DAYS } from '../../utils/iePolicy';

export interface IeBypassConfirmModalProps {
  open: boolean;
  client: ClientResponse | null | undefined;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

const MIN_REASON_LENGTH = 10;
const MAX_REASON_LENGTH = 500;

/**
 * Modal de confirmação quando o usuário tenta criar um contrato Aditivo (fastchip=false)
 * para um cliente com IE inapta/baixada.
 *
 * Exige:
 * - Checkbox de ciência do risco (obrigatório)
 * - Justificativa textual de mínimo 10 caracteres (obrigatória)
 *
 * Ao confirmar, chama onConfirm(reason) — a página seta ieBypassConfirmed=true e
 * ieBypassReason=reason no estado do formulário.
 */
export const IeBypassConfirmModal: React.FC<IeBypassConfirmModalProps> = ({
  open,
  client,
  onConfirm,
  onCancel,
}) => {
  const [ciente, setCiente] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open) {
      setCiente(false);
      setReason('');
    }
  }, [open]);

  // dataUltimaConsultaCnpj existe em ClientResponse mas não está tipado em ClientListResponse.
  // Em runtime o JSON chega como string antes de qualquer cast para Date.
  const rawSync = (client as Record<string, unknown> | null | undefined)?.dataUltimaConsultaCnpj;
  const stale =
    daysSinceLastCnpjSync(rawSync != null ? String(rawSync) : undefined) > CNPJ_SYNC_STALE_DAYS;

  const canConfirm = ciente && reason.trim().length >= MIN_REASON_LENGTH;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm(reason.trim());
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <Modal open={open} size="small" closeOnDimmerClick={false}>
      <Modal.Header
        style={{
          backgroundColor: '#f39c12',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <Icon name="warning sign" />
        Inscrição Estadual Inapta — Risco de Recusa pela TIM
      </Modal.Header>

      <Modal.Content>
        <Message warning>
          <Message.Header>Este cliente possui IE inapta ou baixada no UF</Message.Header>
          <p>
            A TIM recusa contratos de entrega (sem Fastchip) para clientes com Inscrição Estadual
            inapta, baixada ou sem registro ativo no estado. Prosseguir pode resultar na{' '}
            <strong>recusa do pedido no VTME</strong>.
          </p>
        </Message>

        {client && (
          <div
            style={{
              background: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '6px',
              padding: '12px 16px',
              marginBottom: '16px',
            }}
          >
            <div>
              <strong>Cliente:</strong> {client.razaoSocial}
            </div>
            <div>
              <strong>CNPJ:</strong> {client.cnpj}
            </div>
            <div>
              <strong>UF:</strong> {client.estado}
            </div>
            <div style={{ marginTop: '6px' }}>
              <strong>Status IE:</strong>{' '}
              <Label color="red" size="small">
                Inapta / Sem IE ativa
              </Label>
            </div>
            {stale && (
              <div style={{ marginTop: '6px', color: '#856404' }}>
                <Icon name="clock outline" />
                <small>
                  Dados de Inscrição Estadual podem estar desatualizados (última consulta há mais de{' '}
                  {CNPJ_SYNC_STALE_DAYS} dias). Recomendamos verificar manualmente antes de
                  prosseguir.
                </small>
              </div>
            )}
          </div>
        )}

        <Form>
          <Form.Field style={{ marginBottom: '16px' }}>
            <Checkbox
              checked={ciente}
              onChange={(_, data) => setCiente(data.checked ?? false)}
              label={
                <label style={{ fontWeight: 'bold', cursor: 'pointer' }}>
                  Estou ciente do risco e assumo a responsabilidade por prosseguir com este contrato
                </label>
              }
            />
          </Form.Field>

          <Form.Field>
            <label>
              Justificativa <span style={{ color: 'red' }}>*</span>{' '}
              <span style={{ fontWeight: 'normal', color: '#666' }}>
                (mínimo {MIN_REASON_LENGTH} caracteres)
              </span>
            </label>
            <textarea
              rows={3}
              maxLength={MAX_REASON_LENGTH}
              placeholder="Descreva o motivo pelo qual está prosseguindo mesmo com a IE inapta (ex: cliente informou regularização em andamento, negociação aprovada pela gerência, etc.)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: `1px solid ${reason.trim().length > 0 && reason.trim().length < MIN_REASON_LENGTH ? '#e74c3c' : '#ced4da'}`,
                fontFamily: 'inherit',
                fontSize: '14px',
                resize: 'vertical',
              }}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '4px',
                fontSize: '12px',
                color:
                  reason.trim().length > 0 && reason.trim().length < MIN_REASON_LENGTH
                    ? '#e74c3c'
                    : '#666',
              }}
            >
              <span>
                {reason.trim().length > 0 && reason.trim().length < MIN_REASON_LENGTH
                  ? `Mínimo ${MIN_REASON_LENGTH} caracteres`
                  : ''}
              </span>
              <span>
                {reason.length}/{MAX_REASON_LENGTH}
              </span>
            </div>
          </Form.Field>
        </Form>
      </Modal.Content>

      <Modal.Actions>
        <Button onClick={handleCancel}>
          <Icon name="arrow left" />
          Voltar e ajustar
        </Button>
        <Button
          color="orange"
          disabled={!canConfirm}
          onClick={handleConfirm}
        >
          <Icon name="warning sign" />
          Prosseguir com bypass
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
